/**
 * The typed API client.
 *
 * Two things about the staff session shape everything here:
 *
 * 1. **The refresh token is an httpOnly cookie.** This code cannot read it, and
 *    that is the point — an XSS bug in the admin panel cannot walk off with a
 *    30-day session. Every request therefore goes out with
 *    `credentials: 'include'`, and the browser attaches the cookie only to the
 *    `/<prefix>/admin/auth` paths the API scoped it to.
 * 2. **The access token lives in memory only.** Never `localStorage`, never a
 *    non-httpOnly cookie. It dies with the tab, and a reload re-establishes the
 *    session from the cookie instead.
 *
 * Rotation is why refreshes are single-flight: the API marks a presented
 * refresh token used and issues a new one, and presenting a used token revokes
 * the whole family. Two concurrent 401s must therefore produce *one* refresh,
 * not two — the second would look exactly like a stolen token.
 */
import type { TypeOf, ZodTypeAny } from 'zod';

import {
  adminLoginResponseSchema,
  okResponseSchema,
  type AdminLoginRequest,
  type AdminLoginResponse,
} from '@wend/shared';

import { ApiError, isAbortError } from './api-error';
import { API_BASE_URL } from './env';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

export type QueryValue = string | number | boolean | undefined;

interface SendOptions {
  readonly path: string;
  readonly method?: HttpMethod;
  readonly body?: unknown;
  readonly query?: Readonly<Record<string, QueryValue>>;
  readonly signal?: AbortSignal;
}

/**
 * `TSchema` is the response contract from `@wend/shared`. Inferring the return
 * type from the schema — rather than declaring it separately — is what stops
 * this client and the API drifting apart without anyone noticing.
 */
export interface ApiRequest<TSchema extends ZodTypeAny> extends SendOptions {
  readonly schema: TSchema;
  /** `false` for the endpoints that establish a session. Defaults to `true`. */
  readonly authenticated?: boolean;
}

/** Notified whenever the staff session starts, rotates or ends (`null`). */
export type SessionListener = (session: AdminLoginResponse | null) => void;

function buildUrl(path: string, query: Readonly<Record<string, QueryValue>> | undefined): string {
  const url = `${API_BASE_URL}${path}`;
  if (query === undefined) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === '') continue;
    params.set(key, String(value));
  }

  const search = params.toString();
  return search === '' ? url : `${url}?${search}`;
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === '') return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

class AdminApiClient {
  #accessToken: string | null = null;
  #refreshInFlight: Promise<AdminLoginResponse | null> | null = null;
  #listeners = new Set<SessionListener>();

  /** Subscribe to session changes. Returns the unsubscribe function. */
  subscribe(listener: SessionListener): () => void {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  /**
   * Performs a request, transparently refreshing the access token once if the
   * API says it has expired. Resolves with the parsed, contract-checked body;
   * rejects with an `ApiError`.
   */
  async request<TSchema extends ZodTypeAny>(req: ApiRequest<TSchema>): Promise<TypeOf<TSchema>> {
    const authenticated = req.authenticated !== false;

    let response = await this.#send(req, authenticated ? this.#accessToken : null);

    if (authenticated && response.status === 401) {
      // A network failure inside the refresh propagates: it means "we cannot
      // tell", which is not the same as "you are signed out".
      const session = await this.refreshSession();
      if (session === null) throw ApiError.unauthorized();
      response = await this.#send(req, session.accessToken);
    }

    if (!response.ok) throw await ApiError.fromResponse(response);

    const parsed = req.schema.safeParse(await readJson(response));
    if (!parsed.success) throw ApiError.unexpectedResponse(response.status);
    // `safeParse` on a generic `ZodTypeAny` widens its result to `any`; the
    // cast narrows it back to the schema's own output type, which is the whole
    // point of taking the schema as a type parameter.
    return parsed.data as TypeOf<TSchema>;
  }

  /** Email + password. On success the refresh cookie is set by the API. */
  async login(input: AdminLoginRequest): Promise<AdminLoginResponse> {
    const session = await this.request({
      path: '/admin/auth/login',
      method: 'POST',
      body: input,
      schema: adminLoginResponseSchema,
      authenticated: false,
    });

    this.#publish(session);
    return session;
  }

  /**
   * Exchanges the refresh cookie for a new access token.
   *
   * Returns `null` when there is no usable session — that is the ordinary
   * answer on a cold load, not an error. Anything else (the API being
   * unreachable, a 500) rejects, so the caller can tell "signed out" apart from
   * "cannot reach the server".
   */
  async refreshSession(): Promise<AdminLoginResponse | null> {
    const inFlight = this.#refreshInFlight;
    if (inFlight !== null) return inFlight;

    const attempt = this.#performRefresh();
    this.#refreshInFlight = attempt;

    try {
      return await attempt;
    } finally {
      this.#refreshInFlight = null;
    }
  }

  /** Revokes the session server-side and clears the cookie. Throws on failure. */
  async logout(): Promise<void> {
    await this.request({
      path: '/admin/auth/logout',
      method: 'POST',
      schema: okResponseSchema,
      // The cookie authenticates this one; an expired access token must not
      // stop a person signing out.
      authenticated: false,
    });

    this.#publish(null);
  }

  async #performRefresh(): Promise<AdminLoginResponse | null> {
    const response = await this.#send(
      { path: '/admin/auth/refresh', method: 'POST' },
      null,
    );

    if (response.status === 401) {
      this.#publish(null);
      return null;
    }

    if (!response.ok) throw await ApiError.fromResponse(response);

    const parsed = adminLoginResponseSchema.safeParse(await readJson(response));
    if (!parsed.success) throw ApiError.unexpectedResponse(response.status);

    this.#publish(parsed.data);
    return parsed.data;
  }

  async #send(options: SendOptions, token: string | null): Promise<Response> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (token !== null) headers.Authorization = `Bearer ${token}`;

    const init: RequestInit = {
      method: options.method ?? 'GET',
      headers,
      // Required for the httpOnly refresh cookie to travel at all.
      credentials: 'include',
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    };

    try {
      return await fetch(buildUrl(options.path, options.query), init);
    } catch (cause) {
      // An aborted request is the caller's own doing, not a failure.
      if (isAbortError(cause)) throw cause;
      throw ApiError.network(cause);
    }
  }

  #publish(session: AdminLoginResponse | null): void {
    this.#accessToken = session === null ? null : session.accessToken;
    for (const listener of this.#listeners) {
      listener(session);
    }
  }
}

/** One client per tab: it holds the access token and the in-flight refresh. */
export const apiClient = new AdminApiClient();
